<template>
  <div class="relative h-full lg:mt-0 -mt-4 bg-white rounded-t-xl">

    <client-card-large v-if="selectedClient" :customer="selectedClient" />

    <transition name="slide">
      <div v-if="createCustomer"
        class="w-full h-screen bg-white bg-opacity-0 absolute top-0 flex justify-end top-0 left-0 z-50">
        <create-customer v-if="createCustomer" :customer="customer" />
      </div>
    </transition>

    <nav-menu />

    <!-- Header -->
    <div class="w-full bg-gray-50 border-b border-gray-200">
      <div class="px-6 py-4">
        <div class="flex flex-wrap items-center gap-3">
          <h1 class="text-lg font-semibold text-gray-900 mr-4">Client Book</h1>

          <!-- Search -->
          <div class="inline-flex items-center bg-white border border-gray-200 rounded-lg" style="height:40px;width:320px">
            <i class="fa-solid fa-magnifying-glass text-gray-400 text-sm pl-3 flex-shrink-0"></i>
            <input
              type="text"
              v-model="localSearch"
              placeholder="Search company, name, address..."
              class="bg-transparent text-sm pl-2 pr-4 outline-none w-full h-full"
            />
          </div>

          <!-- Search field checkboxes -->
          <div class="hidden sm:flex items-center gap-3 text-xs text-gray-500">
            <label class="flex items-center gap-1 cursor-pointer"><input type="checkbox" v-model="searchFields.company" class="w-3 h-3 rounded" /> Company</label>
            <label class="flex items-center gap-1 cursor-pointer"><input type="checkbox" v-model="searchFields.contact" class="w-3 h-3 rounded" /> Name</label>
            <label class="flex items-center gap-1 cursor-pointer"><input type="checkbox" v-model="searchFields.address" class="w-3 h-3 rounded" /> Address</label>
            <label class="flex items-center gap-1 cursor-pointer"><input type="checkbox" v-model="searchFields.project" class="w-3 h-3 rounded" /> Project</label>
          </div>

          <!-- Show All / Current toggle -->
          <div class="inline-flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden" style="height:40px">
            <button
              @click="showAll = false"
              class="px-4 h-full text-xs font-medium transition-colors"
              :class="!showAll ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'"
            >Current</button>
            <button
              @click="showAll = true"
              class="px-4 h-full text-xs font-medium transition-colors"
              :class="showAll ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'"
            >All</button>
          </div>

          <div class="inline-flex items-center gap-2 ml-auto flex-shrink-0">
            <!-- Bulk Close -->
            <button
              v-if="overdueClosableCount > 0"
              @click="bulkCloseOverdue()"
              class="inline-flex items-center whitespace-nowrap px-4 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 transition-colors flex-shrink-0"
              style="height:40px"
            >
              <i class="fa-solid fa-box-archive mr-1.5"></i>Close {{ overdueClosableCount }} Overdue
            </button>

            <!-- Bulk SMS (disabled — under construction) -->
            <button
              disabled
              class="relative inline-flex items-center whitespace-nowrap px-4 rounded-lg text-sm font-medium bg-gray-300 text-gray-500 cursor-not-allowed flex-shrink-0"
              style="height:40px"
            >
              <i class="fa-solid fa-comments mr-1.5"></i>Bulk SMS
              <span class="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">SOON</span>
            </button>

            <!-- Export CSV -->
            <button
              @click="exportToCSV()"
              class="inline-flex items-center whitespace-nowrap px-4 rounded-lg text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors flex-shrink-0"
              style="height:40px"
            >
              <i class="fa-solid fa-arrow-up-from-bracket mr-1.5"></i>Export CSV
            </button>

            <!-- Add Customer -->
            <button
              @click="createCustomer = true"
              class="inline-flex items-center whitespace-nowrap px-4 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 transition-colors flex-shrink-0"
              style="height:40px"
            >
              <i class="fa-solid fa-plus mr-1.5"></i>Add Customer
            </button>
          </div>
        </div>

        <!-- Filters & Stats bar -->
        <div class="flex flex-wrap items-center gap-4 mt-3 text-xs text-gray-500">
          <!-- Filter by Company -->
          <div class="inline-flex items-center gap-1.5">
            <i class="fa-solid fa-filter text-gray-400" style="font-size:9px"></i>
            <select v-model="filterCompany" class="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white outline-none focus:border-indigo-400" style="max-width:180px">
              <option value="">All Companies</option>
              <option v-for="c in uniqueCompanies" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>

          <!-- Filter by Project -->
          <div class="inline-flex items-center gap-1.5">
            <select v-model="filterProject" class="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white outline-none focus:border-indigo-400" style="max-width:180px">
              <option value="">All Projects</option>
              <option v-for="p in uniqueProjectNames" :key="p" :value="p">{{ p }}</option>
            </select>
          </div>

          <!-- Clear filters -->
          <button v-if="filterCompany || filterProject" @click="filterCompany = ''; filterProject = ''" class="text-indigo-600 hover:text-indigo-800 font-medium">
            <i class="fa-solid fa-xmark mr-0.5"></i>Clear filters
          </button>

          <span class="text-gray-300">|</span>
          <span><span class="font-semibold text-gray-700">{{ filteredClients.length }}</span> clients shown</span>
          <span class="text-gray-300">|</span>
          <span><span class="font-semibold text-green-600">{{ currentProjectCount }}</span> current projects</span>
          <span><span class="font-semibold text-red-600">{{ overdueProjectCount }}</span> overdue</span>
          <span><span class="font-semibold text-gray-400">{{ closedProjectCount }}</span> closed</span>
          <span v-if="warningProjectCount > 0" class="text-amber-600 font-semibold">
            <i class="fa-solid fa-triangle-exclamation mr-0.5"></i>{{ warningProjectCount }} need attention
          </span>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="loadError" class="p-6">
      <div class="bg-red-50 rounded-xl border border-red-200 py-8 px-6 text-left">
        <p class="text-sm font-semibold text-red-700 mb-2"><i class="fa-solid fa-circle-exclamation mr-1.5"></i>Failed to load projects</p>
        <pre class="text-xs text-red-600 whitespace-pre-wrap break-all bg-red-100 rounded-lg p-4 max-h-64 overflow-y-auto">{{ loadError }}</pre>
        <button @click="getItems()" class="mt-4 px-4 py-2 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors">
          <i class="fa-solid fa-rotate-right mr-1"></i>Retry
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-else-if="loading" class="p-6">
      <div class="bg-white rounded-xl border border-gray-200 py-16 text-center">
        <i class="fa-solid fa-spinner fa-spin text-gray-400 text-xl mb-3"></i>
        <p class="text-sm text-gray-400">Loading projects...</p>
        <p v-if="loadStage" class="text-xs text-gray-300 mt-2">{{ loadStage }}</p>
      </div>
    </div>

    <!-- Table -->
    <div v-else-if="filteredClients.length > 0" class="p-6">
      <div class="bg-white rounded-xl border border-gray-200">
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                <th class="py-3 px-4 cursor-pointer hover:text-gray-700 select-none" @click="sortBy('company')">
                  Company
                  <i v-if="sortField === 'company'" :class="sortDir === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'" class="fa-solid ml-1" style="font-size:9px"></i>
                </th>
                <th class="py-3 px-4 cursor-pointer hover:text-gray-700 select-none" @click="sortBy('contact')">
                  Contact
                  <i v-if="sortField === 'contact'" :class="sortDir === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'" class="fa-solid ml-1" style="font-size:9px"></i>
                </th>
                <th class="py-3 px-4">Phone</th>
                <th class="py-3 px-4 text-center">Projects</th>
                <th class="py-3 px-4 text-center cursor-pointer hover:text-gray-700 select-none" @click="sortBy('jobs')">
                  Jobs
                  <i v-if="sortField === 'jobs'" :class="sortDir === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'" class="fa-solid ml-1" style="font-size:9px"></i>
                </th>
                <th class="py-3 px-4 text-center w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in filteredClients"
                :key="row.customerId || index"
                class="border-b border-gray-100 transition-colors hover:bg-gray-50 cursor-pointer"
                @click="openCustomer(row)"
              >
                <!-- Company -->
                <td class="py-3 px-4 font-medium text-gray-900 whitespace-nowrap">
                  {{ row.company }}
                </td>

                <!-- Contact -->
                <td class="py-3 px-4 text-gray-700 whitespace-nowrap">
                  {{ row.contact || '—' }}
                </td>

                <!-- Phone -->
                <td class="py-3 px-4 whitespace-nowrap">
                  <a v-if="row.phone" :href="'tel:' + row.phone" @click.stop class="text-indigo-600 hover:text-indigo-800 text-xs">{{ row.phone }}</a>
                  <span v-else class="text-xs text-gray-300">—</span>
                </td>

                <!-- Projects -->
                <td class="py-3 px-4 text-center">
                  <i v-if="enriching && localSearch" class="fa-solid fa-spinner fa-spin text-gray-300 text-xs"></i>
                  <span v-else class="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    {{ row.projectCount }}
                  </span>
                </td>

                <!-- Jobs -->
                <td class="py-3 px-4 text-center">
                  <i v-if="enriching && localSearch" class="fa-solid fa-spinner fa-spin text-gray-300 text-xs"></i>
                  <span v-else class="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium"
                    :class="row.totalJobCount > 0 ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-300'">
                    {{ row.totalJobCount }}
                  </span>
                </td>

                <!-- Actions -->
                <td class="py-3 px-4 text-center" @click.stop>
                  <div class="flex items-center justify-center gap-1">
                    <button
                      @click="openCustomer(row)"
                      class="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                      title="View customer">
                      <i class="fa-solid fa-eye text-xs"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="p-6">
      <div class="bg-white rounded-xl border border-gray-200 py-16 text-center">
        <i class="fa-solid fa-folder-open text-gray-300 text-2xl mb-3"></i>
        <p class="text-sm text-gray-400">{{ localSearch ? 'No clients match your search' : 'No clients found' }}</p>
      </div>
    </div>

    <!-- Bulk Close Confirmation Modal -->
    <div v-if="bulkCloseModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div class="px-6 py-5 border-b border-gray-200">
          <h3 class="text-lg font-bold text-gray-900">
            <i class="fa-solid fa-box-archive text-amber-600 mr-2"></i>Bulk Close Overdue Projects
          </h3>
        </div>
        <div class="px-6 py-5">
          <p class="text-sm text-gray-600 mb-3">
            This will close <span class="font-semibold">{{ overdueClosableCount }}</span> projects where the estimated end date has passed and there are no remaining jobs.
          </p>
          <p class="text-xs text-gray-400">Closed projects can be reopened later.</p>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button @click="bulkCloseModal = false"
            class="px-4 h-10 rounded-lg text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors">
            Cancel
          </button>
          <button @click="confirmBulkClose()"
            :disabled="bulkClosing"
            class="px-5 h-10 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 transition-colors disabled:opacity-50">
            <i v-if="bulkClosing" class="fa-solid fa-spinner fa-spin mr-1.5"></i>
            {{ bulkClosing ? 'Closing...' : 'Close All' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Close Project Confirmation Modal -->
    <div v-if="closeProjectTarget" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div class="px-6 py-5 border-b border-gray-200">
          <h3 class="text-lg font-bold text-gray-900">
            <i class="fa-solid fa-box-archive text-amber-600 mr-2"></i>Close Project
          </h3>
        </div>
        <div class="px-6 py-5">
          <p class="text-sm text-gray-600 mb-4">Are you sure you want to close this project?</p>
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-400">Company</span>
              <span class="text-sm font-medium text-gray-900">{{ closeProjectTarget.company }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-400">Project</span>
              <span class="text-sm font-medium text-gray-700">
                <span class="text-gray-400 mr-1">#{{ closeProjectTarget.projectId }}</span>{{ closeProjectTarget.projectName }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-400">Contact</span>
              <span class="text-sm text-gray-700">{{ closeProjectTarget.contact || '—' }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-400">Phone</span>
              <span class="text-sm text-gray-700">{{ closeProjectTarget.phone || '—' }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-400">Jobs</span>
              <span class="text-sm text-gray-700">{{ closeProjectTarget.jobCount }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-400">Est. End</span>
              <span class="text-sm text-gray-700">{{ closeProjectTarget.estimatedEndDate ? $moment(closeProjectTarget.estimatedEndDate.seconds * 1000).format("DD/MM/YY") : '—' }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-400">Status</span>
              <span v-if="closeProjectTarget.hasJobsAfterEnd" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                <i class="fa-solid fa-triangle-exclamation mr-1" style="font-size:8px"></i>Needs Review
              </span>
              <span v-else-if="closeProjectTarget.isOverdue" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">
                <i class="fa-solid fa-clock mr-1" style="font-size:8px"></i>Overdue
              </span>
              <span v-else class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                <i class="fa-solid fa-circle mr-1" style="font-size:6px"></i>Active
              </span>
            </div>
          </div>
          <div v-if="closeProjectTarget.hasJobsAfterEnd" class="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p class="text-xs text-amber-700"><i class="fa-solid fa-triangle-exclamation mr-1"></i>This project has jobs scheduled after the estimated end date.</p>
          </div>
          <p class="text-xs text-gray-400 mt-3">Closed projects can be reopened later.</p>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button @click="closeProjectTarget = null"
            class="px-4 h-10 rounded-lg text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors">
            Cancel
          </button>
          <button @click="confirmCloseProject()"
            :disabled="closingProject"
            class="px-5 h-10 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 transition-colors disabled:opacity-50">
            <i v-if="closingProject" class="fa-solid fa-spinner fa-spin mr-1.5"></i>
            {{ closingProject ? 'Closing...' : 'Close Project' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Bulk SMS Modal -->
    <div v-if="bulkSmsModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col" style="max-height:92vh">
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h3 class="text-lg font-bold text-gray-900">
              <i class="fa-solid fa-comments text-indigo-600 mr-2"></i>Bulk Client SMS
            </h3>
            <p class="text-xs text-gray-400 mt-0.5">Send messages to project contacts across all customers</p>
          </div>
          <button @click="closeBulkSms()" class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto" style="min-height:0">
          <!-- Loading -->
          <div v-if="bulkSmsLoading" class="py-16 flex flex-col items-center justify-center">
            <i class="fa-solid fa-spinner fa-spin text-indigo-600 text-2xl mb-3"></i>
            <p class="text-sm text-gray-400">Loading recipients...</p>
          </div>

          <!-- Compose -->
          <div v-if="!bulkSmsSending && !bulkSmsDone && !bulkSmsLoading">
            <!-- Send / Test tab bar -->
            <div class="px-6 pt-4 pb-0">
              <div class="inline-flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden" style="height:36px">
                <button
                  @click="bulkSmsTab = 'send'; testSmsResult = null"
                  class="px-4 h-full text-xs font-medium transition-colors"
                  :class="bulkSmsTab === 'send' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'"
                ><i class="fa-solid fa-paper-plane mr-1.5" style="font-size:10px"></i>Send</button>
                <button
                  @click="bulkSmsTab = 'test'; testSmsResult = null"
                  class="px-4 h-full text-xs font-medium transition-colors"
                  :class="bulkSmsTab === 'test' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'"
                ><i class="fa-solid fa-flask mr-1.5" style="font-size:10px"></i>Test</button>
              </div>
            </div>

            <div class="grid grid-cols-12 divide-x divide-gray-200" style="min-height:400px">
              <!-- Left: Settings & Message -->
              <div class="col-span-7 px-6 py-5">
                <!-- Recipient type -->
                <div class="mb-5">
                  <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block text-left">Recipient Type</label>
                  <div class="flex gap-2">
                    <button @click="bulkSmsRecipientType = 'primary'"
                      class="px-4 py-2 rounded-lg text-xs font-medium transition-colors border"
                      :class="bulkSmsRecipientType === 'primary' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'">
                      <i class="fa-solid fa-user mr-1.5"></i>Primary
                    </button>
                    <button @click="bulkSmsRecipientType = 'secondary'"
                      class="px-4 py-2 rounded-lg text-xs font-medium transition-colors border"
                      :class="bulkSmsRecipientType === 'secondary' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'">
                      <i class="fa-solid fa-user mr-1.5"></i>Secondary
                    </button>
                    <button @click="bulkSmsRecipientType = 'both'"
                      class="px-4 py-2 rounded-lg text-xs font-medium transition-colors border"
                      :class="bulkSmsRecipientType === 'both' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'">
                      <i class="fa-solid fa-users mr-1.5"></i>Both
                    </button>
                  </div>
                  <p class="text-xs text-gray-400 mt-1.5 text-left">{{ bulkSmsRecipientType === 'primary' ? 'Messages will be sent to primary contact phone numbers' : bulkSmsRecipientType === 'secondary' ? 'Messages will be sent to secondary contact phone numbers' : 'Messages will be sent to both primary and secondary contacts' }}</p>
                </div>

                <!-- Filters -->
                <div class="mb-5">
                  <button @click="bulkSmsFilterModal = !bulkSmsFilterModal"
                    class="flex items-center w-full text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    <i class="fa-solid fa-filter mr-1.5"></i>Filters
                    <i :class="bulkSmsFilterModal ? 'fa-chevron-up' : 'fa-chevron-down'" class="fa-solid ml-auto text-gray-400" style="font-size:9px"></i>
                  </button>
                  <div v-if="bulkSmsFilterModal" class="border border-gray-200 rounded-lg p-4 space-y-3">
                    <div>
                      <label class="text-xs text-gray-500 block text-left mb-1">Project starts between</label>
                      <div class="flex gap-2">
                        <flat-pickr v-model="filterStartFrom" :config="dateConfig" placeholder="From"
                          class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400" />
                        <flat-pickr v-model="filterStartTo" :config="dateConfig" placeholder="To"
                          class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400" />
                      </div>
                    </div>
                    <div>
                      <label class="text-xs text-gray-500 block text-left mb-1">Project ends between</label>
                      <div class="flex gap-2">
                        <flat-pickr v-model="filterEndFrom" :config="dateConfig" placeholder="From"
                          class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400" />
                        <flat-pickr v-model="filterEndTo" :config="dateConfig" placeholder="To"
                          class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400" />
                      </div>
                    </div>
                    <div>
                      <label class="text-xs text-gray-500 block text-left mb-1">Jobs in project</label>
                      <div class="flex gap-2">
                        <input v-model="filterMinJobs" type="number" min="0" placeholder="Min"
                          class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400" />
                        <input v-model="filterMaxJobs" type="number" min="0" placeholder="Max"
                          class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400" />
                      </div>
                    </div>
                    <div class="flex gap-2">
                      <button @click="applyFilters()"
                        class="px-4 py-2 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors">
                        Apply
                      </button>
                      <button @click="clearFilters()"
                        class="px-4 py-2 rounded-lg text-xs font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors">
                        Clear
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Template selector -->
                <div v-if="smsTemplates.length > 0" class="mb-5">
                  <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block text-left">Use Template</label>
                  <select v-model="selectedTemplateId" @change="applyTemplate()"
                    class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400 bg-white">
                    <option value="">Custom message</option>
                    <option v-for="t in smsTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
                  </select>
                </div>

                <!-- Message template -->
                <div class="mb-5">
                  <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block text-left">Message Template</label>
                  <textarea v-model="bulkSmsMessage" rows="5"
                    class="w-full rounded-lg border border-gray-200 p-3 text-sm text-gray-700 outline-none focus:border-indigo-400 transition-colors resize-none"></textarea>
                  <div class="mt-2">
                    <p class="text-xs text-gray-400 mb-1.5 text-left">Shortcodes — click to insert:</p>
                    <div class="flex flex-wrap gap-1.5">
                      <button v-for="sc in bulkSmsShortcodes" :key="sc.code" @click="insertShortcode(sc.code)"
                        class="inline-flex items-center px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium hover:bg-indigo-100 transition-colors cursor-pointer">
                        <code class="mr-1">{{ sc.code }}</code>
                        <span class="text-indigo-400 font-normal">{{ sc.label }}</span>
                      </button>
                    </div>
                  </div>
                  <button @click="saveDefaultMessage()"
                    class="mt-3 px-3 py-1.5 rounded-lg text-xs font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors">
                    <i class="fa-solid fa-floppy-disk mr-1"></i>Save as Default
                  </button>
                </div>
              </div>

              <!-- Right: Recipients (Send tab) -->
              <div v-if="bulkSmsTab === 'send'" class="col-span-5 flex flex-col">
                <div class="px-4 py-3 border-b border-gray-200 flex-shrink-0">
                  <div class="flex items-center justify-between">
                    <label class="text-xs font-medium text-gray-500 uppercase tracking-wide text-left">
                      Recipients
                    </label>
                    <span class="text-xs font-semibold text-indigo-600">{{ bulkSmsSelectedCount }} / {{ bulkSmsRecipients.length }}</span>
                  </div>
                  <div class="flex items-center gap-3 mt-2">
                    <button @click="bulkSmsSelectAll()" class="text-xs text-indigo-600 hover:text-indigo-800 font-medium">Select All</button>
                    <span class="text-gray-300">|</span>
                    <button @click="bulkSmsDeselectAll()" class="text-xs text-gray-500 hover:text-gray-700 font-medium">Deselect All</button>
                  </div>
                </div>
                <div class="flex-1 overflow-y-auto">
                  <div v-if="bulkSmsRecipients.length === 0" class="flex flex-col items-center justify-center h-full py-10 px-4">
                    <i class="fa-solid fa-users-slash text-gray-300 text-2xl mb-3"></i>
                    <p class="text-sm text-gray-400 text-center">No recipients found</p>
                    <p class="text-xs text-gray-300 text-center mt-1">No active projects with contact phone numbers</p>
                  </div>
                  <label v-for="(r, idx) in bulkSmsRecipients" :key="idx"
                    class="flex items-start px-4 py-3 cursor-pointer transition-colors border-b border-gray-100"
                    :class="r.selected ? 'bg-indigo-50 hover:bg-indigo-100' : 'hover:bg-gray-50'">
                    <input type="checkbox" v-model="r.selected" class="w-4 h-4 rounded border-gray-300 text-indigo-600 cursor-pointer mt-0.5 flex-shrink-0" />
                    <div class="ml-3 flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-800 truncate text-left">{{ r.contact || r.company || 'Unknown' }}</p>
                      <p v-if="r.company && r.contact" class="text-xs text-gray-400 truncate text-left">{{ r.company }}</p>
                      <p class="text-xs text-gray-400 truncate text-left">{{ r.projectName }}</p>
                      <div class="flex items-center gap-3 mt-1">
                        <span v-if="r.phone" class="text-xs text-gray-500 text-left"><i class="fa-solid fa-phone text-gray-300 mr-1" style="font-size:9px"></i>{{ r.phone }}</span>
                        <span class="text-xs text-gray-400">{{ r.jobCount }} {{ r.jobCount === 1 ? 'job' : 'jobs' }}</span>
                      </div>
                    </div>
                    <div class="flex-shrink-0 ml-2 mt-0.5">
                      <span v-if="!r.phone" class="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-600">No phone</span>
                    </div>
                  </label>
                </div>
              </div>

              <!-- Right: Test SMS panel -->
              <div v-else class="col-span-5 flex flex-col">
                <div class="px-4 py-3 border-b border-gray-200 flex-shrink-0">
                  <label class="text-xs font-medium text-gray-500 uppercase tracking-wide text-left block">
                    Test SMS
                  </label>
                  <p class="text-xs text-gray-400 mt-1 text-left">Send a single test message to verify shortcodes and formatting</p>
                </div>
                <div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                  <!-- Phone input -->
                  <div>
                    <label class="text-xs font-medium text-gray-500 block text-left mb-1">Phone Number</label>
                    <input
                      v-model="testSmsPhone"
                      type="text"
                      placeholder="+61..."
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400 transition-colors"
                    />
                  </div>

                  <!-- Message preview -->
                  <div>
                    <label class="text-xs font-medium text-gray-500 block text-left mb-1">Message Preview</label>
                    <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap break-words" style="min-height:80px">{{ testSmsPreview }}</div>
                  </div>

                  <!-- Result -->
                  <div v-if="testSmsResult">
                    <div v-if="testSmsResult.success" class="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <i class="fa-solid fa-circle-check text-green-600"></i>
                      <span class="text-sm text-green-700 font-medium">Test message sent successfully</span>
                    </div>
                    <div v-else class="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <i class="fa-solid fa-circle-xmark text-red-600"></i>
                      <span class="text-sm text-red-700 font-medium">{{ testSmsResult.error || 'Failed to send test message' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sending progress -->
          <div v-if="bulkSmsSending && !bulkSmsDone" class="py-16 px-6">
            <div class="flex flex-col items-center max-w-sm mx-auto">
              <div class="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                <i class="fa-solid fa-paper-plane text-indigo-600 text-2xl animate-pulse"></i>
              </div>
              <p class="text-sm font-semibold text-gray-800 mb-1">Sending messages...</p>
              <p class="text-xs text-gray-400 mb-4">{{ bulkSmsSent }} of {{ bulkSmsTotal }} sent</p>
              <div class="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div class="bg-indigo-600 h-3 rounded-full transition-all duration-300"
                  :style="{ width: bulkSmsProgress + '%' }"></div>
              </div>
              <p class="text-xs text-gray-400 mt-2">{{ bulkSmsProgress }}% complete</p>
            </div>
          </div>

          <!-- Results -->
          <div v-if="bulkSmsDone" class="py-10 px-6">
            <div class="flex flex-col items-center mb-6">
              <div class="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                :class="bulkSmsFailedCount > 0 ? 'bg-yellow-100' : 'bg-green-100'">
                <i class="text-2xl" :class="bulkSmsFailedCount > 0 ? 'fa-solid fa-triangle-exclamation text-yellow-600' : 'fa-solid fa-circle-check text-green-600'"></i>
              </div>
              <p class="text-lg font-bold text-gray-900">
                {{ bulkSmsFailedCount > 0 ? 'Sent with errors' : 'All messages sent!' }}
              </p>
              <p class="text-sm text-gray-500">{{ bulkSmsSuccessCount }} sent{{ bulkSmsFailedCount > 0 ? ', ' + bulkSmsFailedCount + ' failed' : '' }}</p>
            </div>

            <div class="max-w-lg mx-auto border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-52 overflow-y-auto">
              <div v-for="(r, idx) in bulkSmsResults" :key="idx"
                class="flex items-center px-4 py-2.5 text-sm">
                <i :class="r.success ? 'fa-solid fa-circle-check text-green-500' : 'fa-solid fa-circle-xmark text-red-500'" class="mr-3"></i>
                <span class="text-gray-700 flex-1 truncate text-left">{{ r.phone }}</span>
                <span v-if="r.success" class="text-xs text-green-600 font-medium">Sent</span>
                <span v-else class="text-xs text-red-600 font-medium truncate" style="max-width:160px">{{ r.error || 'Failed' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <div v-if="!bulkSmsSending && !bulkSmsDone" class="flex items-center justify-between">
            <div class="text-xs text-gray-400 text-left">
              <i class="fa-solid fa-info-circle mr-1"></i>
              Messages sent via Twilio to client phone numbers
            </div>
            <div class="flex items-center gap-2">
              <button @click="closeBulkSms()"
                class="px-4 h-10 rounded-lg text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors">
                Cancel
              </button>
              <!-- Send tab: bulk send button -->
              <button v-if="bulkSmsTab === 'send'" @click="sendBulkSms()"
                :disabled="bulkSmsSelectedCount === 0 || !bulkSmsMessage.trim()"
                class="px-5 h-10 rounded-lg text-sm font-medium inline-flex items-center whitespace-nowrap bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <i class="fa-solid fa-paper-plane mr-2" style="font-size:11px"></i>
                Send {{ bulkSmsSelectedCount }} {{ bulkSmsSelectedCount === 1 ? 'Message' : 'Messages' }}
              </button>
              <!-- Test tab: test send button -->
              <button v-else @click="sendTestSms()"
                :disabled="!testSmsPhone.trim() || !bulkSmsMessage.trim() || testSmsSending"
                class="px-5 h-10 rounded-lg text-sm font-medium inline-flex items-center whitespace-nowrap bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <i v-if="testSmsSending" class="fa-solid fa-spinner fa-spin mr-2" style="font-size:11px"></i>
                <i v-else class="fa-solid fa-flask mr-2" style="font-size:11px"></i>
                {{ testSmsSending ? 'Sending...' : 'Send Test' }}
              </button>
            </div>
          </div>
          <div v-else-if="bulkSmsDone" class="flex justify-end">
            <button @click="closeBulkSms()"
              class="px-5 h-10 rounded-lg text-sm font-medium bg-gray-900 text-white hover:opacity-90 transition-colors">
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { postFire, setFire, deleteFire, putFire, getFire, getJobsByCustomerId } from "../firestore/utils";
import { getTimeStamp, auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { bus } from "../main";

var sortJsonArray = require("sort-json-array");
import { Parser } from "json2csv";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import CreateCustomer from "@/components/cards/createCustomer.vue";
import ClientCardLarge from "@/components/cards/clientCardLarge.vue";
import NavMenu from "@/components/headers/navMenu"
import { applyCustomFieldTokens, loadDefinitions } from "../services/customFields";
export default {
  props: ["date", "prevDate", "search", "triggerExport"],
  data() {
    return {
      selectedClient: null,
      createCustomer: false,
      loading: false,
      enriching: false,
      loadError: null,
      loadStage: '',
      localSearch: '',
      showAll: false,
      sortField: 'company',
      sortDir: 'asc',
      searchFields: { company: true, contact: true, address: true, project: true },
      filterCompany: '',
      filterProject: '',

      // Flat project rows: one row per project
      allProjects: [],
      // Customer lookup for opening cards
      customerMap: {},

      // Close project
      closeProjectTarget: null,
      closingProject: false,

      // Bulk close
      bulkCloseModal: false,
      bulkClosing: false,

      // Bulk SMS
      bulkSmsModal: false,
      bulkSmsMessage: '',
      bulkSmsDefaultMessage: 'Hi {recipientName} - checking in to see if there are any jobs this week.',
      bulkSmsRecipients: [],
      bulkSmsRecipientType: 'primary',
      bulkSmsSending: false,
      bulkSmsSent: 0,
      bulkSmsTotal: 0,
      bulkSmsResults: [],
      bulkSmsDone: false,
      bulkSmsLoading: false,

      // Test SMS
      bulkSmsTab: 'send',
      testSmsPhone: '',
      testSmsSending: false,
      testSmsResult: null,

      // SMS Templates
      smsTemplates: [],
      customFieldDefs: null,
      selectedTemplateId: '',

      // SMS Filters
      bulkSmsFilterModal: false,
      filterStartFrom: null,
      filterStartTo: null,
      filterEndFrom: null,
      filterEndTo: null,
      filterMinJobs: '',
      filterMaxJobs: '',
      dateConfig: { enableTime: false, altFormat: 'd-m-Y', altInput: true, allowInput: false },
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    clientRows() {
      let projects = this.allProjects;
      if (!this.showAll) {
        projects = projects.filter(r => !r.closed);
      }
      const grouped = {};
      projects.forEach(p => {
        const key = (p.company || '').trim().toLowerCase();
        if (!key) return;
        if (!grouped[key]) {
          grouped[key] = {
            customerId: p.customerId || '',
            company: p.company || '',
            contact: p.contact || '',
            phone: p.phone || '',
            projectCount: 0,
            totalJobCount: 0,
          };
        }
        grouped[key].projectCount++;
        grouped[key].totalJobCount += (p.jobCount || 0);
        // Keep the first non-empty customerId for opening the card
        if (!grouped[key].customerId && p.customerId) {
          grouped[key].customerId = p.customerId;
        }
      });
      return Object.values(grouped);
    },
    uniqueCompanies() {
      const set = new Set();
      this.allProjects.forEach(p => { if (p.company) set.add(p.company); });
      return [...set].sort((a, b) => a.localeCompare(b));
    },
    uniqueProjectNames() {
      const set = new Set();
      this.allProjects.forEach(p => { if (p.projectName) set.add(p.projectName); });
      return [...set].sort((a, b) => a.localeCompare(b));
    },
    filteredClients() {
      // Start with projects, apply dropdown filters first
      let projects = this.allProjects;
      if (!this.showAll) {
        projects = projects.filter(r => !r.closed);
      }
      if (this.filterCompany) {
        projects = projects.filter(r => r.company === this.filterCompany);
      }
      if (this.filterProject) {
        projects = projects.filter(r => r.projectName === this.filterProject);
      }

      // If searching, filter projects by search term then group matching customers
      if (this.localSearch && this.localSearch.trim()) {
        const q = this.localSearch.trim().toLowerCase();
        const sf = this.searchFields;
        projects = projects.filter(p => {
          if (sf.company && (p.company || '').toLowerCase().includes(q)) return true;
          if (sf.contact && ((p.contact || '').toLowerCase().includes(q) || (p.secondaryContact || '').toLowerCase().includes(q))) return true;
          if (sf.address && (p.formattedAddress || '').toLowerCase().includes(q)) return true;
          if (sf.project && (p.projectName || '').toLowerCase().includes(q)) return true;
          if ((p.phone || '').includes(q)) return true;
          return false;
        });
      }

      // Group filtered projects by company name (case-insensitive)
      const grouped = {};
      projects.forEach(p => {
        const key = (p.company || '').trim().toLowerCase();
        if (!key) return;
        if (!grouped[key]) {
          grouped[key] = {
            customerId: p.customerId || '',
            company: p.company || '',
            contact: p.contact || '',
            phone: p.phone || '',
            projectCount: 0,
            totalJobCount: 0,
          };
        }
        grouped[key].projectCount++;
        grouped[key].totalJobCount += (p.jobCount || 0);
        if (!grouped[key].customerId && p.customerId) {
          grouped[key].customerId = p.customerId;
        }
      });

      // When searching, also include customers that match but have no filtered projects
      if (this.localSearch && this.localSearch.trim() && this.searchFields.company) {
        const q = this.localSearch.trim().toLowerCase();
        Object.values(this.customerMap).forEach(c => {
          const companyName = (c.company || '').trim();
          const key = companyName.toLowerCase();
          if (!key || grouped[key]) return;
          if (key.includes(q)) {
            const custProjects = this.allProjects.filter(p => p.customerId === c.id);
            grouped[key] = {
              customerId: c.id || '',
              company: companyName,
              contact: c.contact || '',
              phone: c.phone || '',
              projectCount: custProjects.length,
              totalJobCount: custProjects.reduce((sum, p) => sum + (p.jobCount || 0), 0),
            };
          }
        });
      }

      let rows = Object.values(grouped);

      const dir = this.sortDir === 'asc' ? 1 : -1;
      const field = this.sortField;
      rows = [...rows].sort((a, b) => {
        if (field === 'company') return (a.company || '').localeCompare(b.company || '') * dir;
        if (field === 'contact') return (a.contact || '').localeCompare(b.contact || '') * dir;
        if (field === 'jobs') return ((a.totalJobCount || 0) - (b.totalJobCount || 0)) * dir;
        return 0;
      });

      return rows;
    },
    filteredProjects() {
      let rows = this.allProjects;

      if (!this.showAll) {
        rows = rows.filter(r => !r.closed);
      }

      if (this.localSearch && this.localSearch.trim()) {
        const q = this.localSearch.trim().toLowerCase();
        rows = rows.filter(r =>
          (r.company || '').toLowerCase().includes(q) ||
          (r.projectName || '').toLowerCase().includes(q) ||
          (r.contact || '').toLowerCase().includes(q) ||
          (r.secondaryContact || '').toLowerCase().includes(q) ||
          (r.phone || '').includes(q) ||
          String(r.projectId).includes(q)
        );
      }

      return rows;
    },
    currentProjectCount() {
      return this.allProjects.filter(r => !r.closed && !r.isOverdue).length;
    },
    overdueProjectCount() {
      return this.allProjects.filter(r => !r.closed && r.isOverdue).length;
    },
    closedProjectCount() {
      return this.allProjects.filter(r => r.closed).length;
    },
    warningProjectCount() {
      return this.allProjects.filter(r => !r.closed && r.hasJobsAfterEnd).length;
    },
    overdueClosableCount() {
      return this.allProjects.filter(r => !r.closed && r.isOverdue && r.jobCount === 0 && !r.hasJobsAfterEnd).length;
    },
    bulkSmsShortcodes() {
      return [
        { code: '{recipientName}', label: 'Recipient name' },
        { code: '{company}', label: 'Company' },
        { code: '{project}', label: 'Project name' },
        { code: '{contact}', label: 'Primary contact' },
      ];
    },
    bulkSmsSelectedCount() {
      return this.bulkSmsRecipients.filter(r => r.selected).length;
    },
    bulkSmsProgress() {
      if (!this.bulkSmsTotal) return 0;
      return Math.round((this.bulkSmsSent / this.bulkSmsTotal) * 100);
    },
    bulkSmsSuccessCount() {
      return this.bulkSmsResults.filter(r => r.success).length;
    },
    bulkSmsFailedCount() {
      return this.bulkSmsResults.filter(r => !r.success).length;
    },
    testSmsPreview() {
      const firstSelected = this.bulkSmsRecipients.find(r => r.selected);
      const sampleData = firstSelected
        ? { contact: firstSelected.contact, company: firstSelected.company, projectName: firstSelected.projectName }
        : { contact: 'Contact Name', company: 'Company', projectName: 'Project' };
      return this.buildSmsBody(this.bulkSmsMessage, sampleData);
    },
  },
  watch: {
    search(val) {
      this.localSearch = val || '';
    },
  },
  methods: {
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDir = 'asc';
      }
    },
    openCustomer(row) {
      const customer = this.customerMap[row.customerId];
      if (customer) this.selectedClient = customer;
    },
    isOverdue(estimatedEndDate) {
      if (!estimatedEndDate) return false;
      const endDate = this.$moment(estimatedEndDate.seconds * 1000);
      return endDate.isBefore(this.$moment(), 'day');
    },
    hasJobsAfterEndDate(jobs, estimatedEndDate) {
      if (!estimatedEndDate || !jobs || !jobs.length) return false;
      const endMs = estimatedEndDate.seconds * 1000;
      return jobs.some(j => {
        if (!j.date) return false;
        const jobMs = j.date.seconds ? j.date.seconds * 1000 : new Date(j.date).getTime();
        return jobMs > endMs;
      });
    },
    promptCloseProject(row) {
      this.closeProjectTarget = row;
      this.closingProject = false;
    },
    async confirmCloseProject() {
      if (!this.closeProjectTarget) return;
      this.closingProject = true;
      try {
        await putFire({
          collection: 'projects',
          doc: this.closeProjectTarget.projectDocId,
          body: { closed: true },
        });
        this.closeProjectTarget.closed = true;
        this.closeProjectTarget = null;
      } catch (e) {
        console.error('Failed to close project:', e);
      }
      this.closingProject = false;
    },
    async reopenProject(row) {
      try {
        await putFire({
          collection: 'projects',
          doc: row.projectDocId,
          body: { closed: false },
        });
        row.closed = false;
      } catch (e) {
        console.error('Failed to reopen project:', e);
      }
    },
    bulkCloseOverdue() {
      this.bulkCloseModal = true;
    },
    async confirmBulkClose() {
      this.bulkClosing = true;
      const targets = this.allProjects.filter(r => !r.closed && r.isOverdue && r.jobCount === 0 && !r.hasJobsAfterEnd);
      for (const row of targets) {
        try {
          await putFire({
            collection: 'projects',
            doc: row.projectDocId,
            body: { closed: true },
          });
          row.closed = true;
        } catch (e) {
          console.error('Failed to close project:', row.projectDocId, e);
        }
      }
      this.bulkClosing = false;
      this.bulkCloseModal = false;
    },
    async deleteProject(i) {
      if (window.confirm('Are you sure you want to delete?')) {
        await deleteFire({ collection: "customers", doc: i.id });
        const _jobs = await getJobsByCustomerId(i.id);
        for (let i = 0; i < _jobs.length; i++) {
          await deleteFire({ collection: "jobs", doc: _jobs[i].id });
        }
        this.getItems();
      }
    },
    exportToCSV() {
      let transformedData = [];

      this.allProjects.forEach(row => {
        transformedData.push({
          company: row.company,
          projectId: row.projectId,
          projectName: row.projectName,
          contact: row.contact,
          phone: row.phone,
          secondaryContact: row.secondaryContact,
          secondaryPhone: row.secondaryPhone,
          email: row.email,
          address: row.formattedAddress,
          estimatedStartDate: row.estimatedStartDate ? this.$moment(row.estimatedStartDate.seconds * 1000).format("DD/MM/YYYY") : "",
          estimatedEndDate: row.estimatedEndDate ? this.$moment(row.estimatedEndDate.seconds * 1000).format("DD/MM/YYYY") : "",
          jobCount: row.jobCount,
          closed: row.closed ? 'Yes' : 'No',
          isOverdue: row.isOverdue ? 'Yes' : 'No',
          hasJobsAfterEnd: row.hasJobsAfterEnd ? 'Yes' : 'No',
          poNumber: row.poNumber,
          region: row.region,
        });
      });

      const json2csvParser = new Parser();
      const csvData = json2csvParser.parse(transformedData);

      const blob = new Blob([csvData], { type: "text/csv" });
      const anchor = document.createElement("a");
      anchor.href = window.URL.createObjectURL(blob);
      anchor.download = "client_book_projects.csv";
      anchor.click();
      window.URL.revokeObjectURL(anchor.href);
      anchor.remove();
    },
    async getItems() {
      this.loading = true;
      this.loadError = null;
      this.loadStage = '';
      this.allProjects = [];
      this.customerMap = {};

      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const apiBase = 'https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1';
        const headers = { Authorization: `Bearer ${token}` };

        // Phase 1: 2 parallel API calls — customers + projects — renders table instantly
        const [custRes, projRes] = await Promise.all([
          fetch(`${apiBase}/customers`, { headers }),
          fetch(`${apiBase}/projects?active=true`, { headers }),
        ]);
        if (!custRes.ok) throw new Error(`Customers API error: ${custRes.status}`);
        if (!projRes.ok) throw new Error(`Projects API error: ${projRes.status}`);

        const custJson = await custRes.json();
        const projJson = await projRes.json();
        const customers = custJson.data || custJson || [];
        const projects = projJson.data || projJson || [];

        // Build customer lookup
        customers.forEach(c => {
          this.customerMap[c.id] = c;
        });

        // Build flat project rows (no job data yet)
        const rows = [];
        projects.forEach(p => {
          if (p.active === false) return;
          const customer = this.customerMap[p.customerId];
          if (!customer) return;

          const estEnd = p.estimatedEndDate || null;
          rows.push({
            company: customer.company || '',
            customerId: p.customerId,
            projectDocId: p.id,
            projectId: p.projectId || '',
            projectName: p.projectName || '',
            contact: p.contact || '',
            phone: p.phone || '',
            email: p.email || '',
            secondaryContact: p.secondaryContact || '',
            secondaryPhone: p.secondaryPhone || '',
            estimatedStartDate: p.estimatedStartDate || null,
            estimatedEndDate: estEnd,
            poNumber: p.poNumber || '',
            region: p.region || '',
            formattedAddress: p.formattedAddress || '',
            jobCount: 0,
            closed: p.closed || false,
            isOverdue: this.isOverdue(estEnd),
            hasJobsAfterEnd: false,
          });
        });

        this.allProjects = rows;
        this.loading = false;
        this.loadStage = '';

        // Phase 2: fetch jobs in background — enrich with counts + warnings
        this.enrichWithJobs();
      } catch (e) {
        console.error("Client Book load error:", e);
        this.loadError = (e.message || String(e)) + (e.code ? '\n\nCode: ' + e.code : '') + (e.stack ? '\n\n' + e.stack : '');
        this.loading = false;
        this.loadStage = '';
      }
    },
    async enrichWithJobs() {
      this.enriching = true;
      try {
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const apiBase = 'https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1';
        const jobsRes = await fetch(`${apiBase}/jobs`, { headers: { Authorization: `Bearer ${token}` } });
        if (!jobsRes.ok) throw new Error(`Jobs API error: ${jobsRes.status}`);
        const jobsJson = await jobsRes.json();
        const allJobs = jobsJson.data || jobsJson || [];

        // Index jobs by customerId::projectId
        const jobIndex = {};
        allJobs.forEach(j => {
          const key = (j.customerId || '') + '::' + (j.projectId || '');
          if (!jobIndex[key]) jobIndex[key] = [];
          jobIndex[key].push(j);
        });

        // Enrich each row
        for (const row of this.allProjects) {
          const key = row.customerId + '::' + row.projectId;
          const projectJobs = jobIndex[key] || [];
          row.jobCount = projectJobs.length;
          row.hasJobsAfterEnd = this.hasJobsAfterEndDate(projectJobs, row.estimatedEndDate);
        }

        // Force Vue reactivity update
        this.allProjects = [...this.allProjects];
      } catch (e) {
        console.error("Failed to load jobs for enrichment:", e);
      } finally {
        this.enriching = false;
      }
    },

    // ── Bulk SMS ──
    async getCurrentUser() {
      return new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          user ? resolve(user) : reject(new Error("No user signed in."));
        });
      });
    },
    normalizePhoneForSms(phone) {
      if (!phone) return '';
      let p = phone.replace(/[\s\-\(\)]/g, '');
      if (p.startsWith('04')) p = '+61' + p.slice(1);
      if (!p.startsWith('+')) p = '+' + p;
      return p;
    },
    async openBulkSmsModal() {
      this.bulkSmsModal = true;
      this.bulkSmsSending = false;
      this.bulkSmsDone = false;
      this.bulkSmsResults = [];
      this.bulkSmsSent = 0;
      this.bulkSmsTotal = 0;
      this.bulkSmsRecipientType = 'primary';
      this.bulkSmsTab = 'send';
      this.testSmsPhone = '';
      this.testSmsSending = false;
      this.testSmsResult = null;
      this.bulkSmsLoading = true;

      const defaultMsg = this.currentUser.clientBookDefaultSms || this.bulkSmsDefaultMessage;
      this.bulkSmsMessage = defaultMsg;

      this.filterStartFrom = null;
      this.filterStartTo = null;
      this.filterEndFrom = null;
      this.filterEndTo = null;
      this.filterMinJobs = '';
      this.filterMaxJobs = '';
      this.bulkSmsFilterModal = false;

      this.selectedTemplateId = '';
      this.loadSmsTemplates();
      this.buildRecipients();
      this.bulkSmsLoading = false;
    },
    async loadSmsTemplates() {
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const res = await fetch(
          'https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/settings/sms/templates',
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        // Service-scoped templates are excluded: a bulk send spans jobs from
        // every service, so their wording would be wrong for most recipients.
        this.smsTemplates = (json.data || [])
          .filter(t => t.type === 'bulk' || t.type === 'clientBook' || t.type === 'all')
          .filter(t => !t.service || t.service === 'all');
      } catch (e) {
        console.error('Failed to load SMS templates:', e);
        this.smsTemplates = [];
      }
    },
    applyTemplate() {
      if (!this.selectedTemplateId) return;
      const tpl = this.smsTemplates.find(t => t.id === this.selectedTemplateId);
      if (tpl) this.bulkSmsMessage = tpl.body;
    },
    buildRecipients() {
      const recipients = [];
      // Include all projects (regardless of job status or closed state)
      const source = this.allProjects;

      for (const p of source) {
        // Apply date filters
        if (this.filterStartFrom && p.estimatedStartDate) {
          const start = this.$moment(p.estimatedStartDate.seconds * 1000);
          if (start.isBefore(this.$moment(this.filterStartFrom), 'day')) continue;
        }
        if (this.filterStartTo && p.estimatedStartDate) {
          const start = this.$moment(p.estimatedStartDate.seconds * 1000);
          if (start.isAfter(this.$moment(this.filterStartTo), 'day')) continue;
        }
        if (this.filterEndFrom && p.estimatedEndDate) {
          const end = this.$moment(p.estimatedEndDate.seconds * 1000);
          if (end.isBefore(this.$moment(this.filterEndFrom), 'day')) continue;
        }
        if (this.filterEndTo && p.estimatedEndDate) {
          const end = this.$moment(p.estimatedEndDate.seconds * 1000);
          if (end.isAfter(this.$moment(this.filterEndTo), 'day')) continue;
        }
        if (this.filterMinJobs !== '' && (p.jobCount || 0) < parseInt(this.filterMinJobs)) continue;
        if (this.filterMaxJobs !== '' && (p.jobCount || 0) > parseInt(this.filterMaxJobs)) continue;
        if ((this.filterStartFrom || this.filterStartTo) && !p.estimatedStartDate) continue;
        if ((this.filterEndFrom || this.filterEndTo) && !p.estimatedEndDate) continue;

        recipients.push({
          company: p.company || '',
          contact: p.contact || '',
          phone: p.phone || '',
          email: p.email || '',
          secondaryContact: p.secondaryContact || '',
          secondaryPhone: p.secondaryPhone || '',
          projectName: p.projectName || '',
          projectId: p.projectId || '',
          customerId: p.customerId || '',
          estimatedStartDate: p.estimatedStartDate,
          estimatedEndDate: p.estimatedEndDate,
          jobCount: p.jobCount || 0,
          selected: !!(p.phone),
        });
      }
      this.bulkSmsRecipients = recipients;
    },
    applyFilters() {
      this.buildRecipients();
    },
    clearFilters() {
      this.filterStartFrom = null;
      this.filterStartTo = null;
      this.filterEndFrom = null;
      this.filterEndTo = null;
      this.filterMinJobs = '';
      this.filterMaxJobs = '';
      this.buildRecipients();
    },
    async saveDefaultMessage() {
      try {
        const user = await this.getCurrentUser();
        await putFire({
          collection: 'users',
          doc: user.uid,
          body: { clientBookDefaultSms: this.bulkSmsMessage },
        });
        this.$store.commit('updateState', {
          state: 'currentUser',
          body: { ...this.currentUser, clientBookDefaultSms: this.bulkSmsMessage },
        });
        this.bulkSmsDefaultMessage = this.bulkSmsMessage;
        alert('Default message saved.');
      } catch (e) {
        console.error('Failed to save default message:', e);
      }
    },
    buildSmsBody(template, recipient) {
      const body = template
        .replace(/\{recipientName\}/g, recipient.receiverName || recipient.contact || '')
        .replace(/\{company\}/g, recipient.company || '')
        .replace(/\{project\}/g, recipient.projectName || '')
        .replace(/\{contact\}/g, recipient.contact || '');
      // A recipient is not a job, so there is nothing to resolve — this just
      // strips any custom field token rather than sending it out literally.
      return applyCustomFieldTokens(this.customFieldDefs, {}, body);
    },
    async sendBulkSms() {
      const selected = this.bulkSmsRecipients.filter(r => r.selected);
      if (!selected.length || !this.bulkSmsMessage.trim()) return;

      this.bulkSmsSending = true;
      this.bulkSmsDone = false;
      this.bulkSmsResults = [];

      const sendTargets = [];
      for (const r of selected) {
        if (this.bulkSmsRecipientType === 'primary' || this.bulkSmsRecipientType === 'both') {
          if (r.phone) {
            sendTargets.push({
              phone: this.normalizePhoneForSms(r.phone),
              message: this.buildSmsBody(this.bulkSmsMessage, { ...r, receiverName: r.contact }),
              type: 'marketing',
              projectId: r.projectId,
              customerId: r.customerId,
              company: r.company,
              projectName: r.projectName,
            });
          }
        }
        if (this.bulkSmsRecipientType === 'secondary' || this.bulkSmsRecipientType === 'both') {
          if (r.secondaryPhone) {
            sendTargets.push({
              phone: this.normalizePhoneForSms(r.secondaryPhone),
              message: this.buildSmsBody(this.bulkSmsMessage, { ...r, receiverName: r.secondaryContact }),
              type: 'marketing',
              projectId: r.projectId,
              customerId: r.customerId,
              company: r.company,
              projectName: r.projectName,
            });
          }
        }
      }

      this.bulkSmsTotal = sendTargets.length;
      this.bulkSmsSent = 0;

      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();

        const batchSize = 5;
        for (let i = 0; i < sendTargets.length; i += batchSize) {
          const batch = sendTargets.slice(i, i + batchSize);
          const res = await fetch(
            'https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/messages/bulk',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ recipients: batch }),
            }
          );
          const data = await res.json();
          if (data.data && data.data.results) {
            this.bulkSmsResults.push(...data.data.results);
          }
          this.bulkSmsSent += batch.length;
        }
      } catch (e) {
        console.error('Bulk SMS error:', e);
        const sent = this.bulkSmsResults.length;
        for (let i = sent; i < sendTargets.length; i++) {
          this.bulkSmsResults.push({ phone: sendTargets[i].phone, success: false, error: 'Network error' });
        }
        this.bulkSmsSent = this.bulkSmsTotal;
      }

      this.bulkSmsDone = true;
      this.bulkSmsSending = false;
    },
    bulkSmsSelectAll() {
      this.bulkSmsRecipients.forEach(r => { if (r.phone) r.selected = true; });
    },
    bulkSmsDeselectAll() {
      this.bulkSmsRecipients.forEach(r => { r.selected = false; });
    },
    insertShortcode(code) {
      this.bulkSmsMessage += code;
    },
    async sendTestSms() {
      if (!this.testSmsPhone.trim() || !this.bulkSmsMessage.trim()) return;
      this.testSmsSending = true;
      this.testSmsResult = null;

      const phone = this.normalizePhoneForSms(this.testSmsPhone);
      const firstSelected = this.bulkSmsRecipients.find(r => r.selected);
      const sampleData = firstSelected
        ? { receiverName: firstSelected.contact, contact: firstSelected.contact, company: firstSelected.company, projectName: firstSelected.projectName }
        : { receiverName: 'Contact Name', contact: 'Contact Name', company: 'Company', projectName: 'Project' };
      const message = this.buildSmsBody(this.bulkSmsMessage, sampleData);

      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const res = await fetch(
          'https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/messages/bulk',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ recipients: [{ phone, message }] }),
          }
        );
        const data = await res.json();
        if (data.data && data.data.results && data.data.results.length > 0) {
          const r = data.data.results[0];
          this.testSmsResult = { success: r.success, error: r.error || null };
        } else {
          this.testSmsResult = { success: false, error: 'Unexpected response from server' };
        }
      } catch (e) {
        console.error('Test SMS error:', e);
        this.testSmsResult = { success: false, error: e.message || 'Network error' };
      }

      this.testSmsSending = false;
    },
    closeBulkSms() {
      this.bulkSmsModal = false;
      this.bulkSmsSending = false;
      this.bulkSmsDone = false;
      this.bulkSmsResults = [];
    },
  },
  created() {
    loadDefinitions().then((defs) => { this.customFieldDefs = defs; });
    bus.$on("openClientCardLarge", (data) => {
      this.selectedClient = data;
    });
    bus.$on("closeCreateCustomer", (data) => {
      this.createCustomer = data.status;
    });
    bus.$on("closeClientCard", (data) => {
      this.selectedClient = null;
    });

    this.getItems();
  },
  components: {
    ClientCardLarge,
    CreateCustomer,
    NavMenu,
    flatPickr,
  },
};
</script>
